/**
 * FeeManager.js — Fee Structure Management
 * ==========================================
 * Per-deal, per-investor fee routing with full CRUD
 */

import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import FinTable from "../components/FinTable";

const C = {
  bg: "#0f1724", card: "#1a2332", border: "#2a3a4e", text: "#e2e8f0",
  muted: "#94a3b8", accent: "#3b82f6", green: "#22c55e", greenBg: "rgba(34,197,94,.12)",
  red: "#ef4444", redBg: "rgba(239,68,68,.12)", orange: "#f59e0b",
  orangeBg: "rgba(245,158,11,.12)", purple: "#a855f7", purpleBg: "rgba(168,85,247,.12)",
  cyan: "#06b6d4", cyanBg: "rgba(6,182,212,.12)",
};

const fmtUSD = (v) => v != null ? Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "\u2014";
const fmtPct = (v) => v != null ? `${Number(v).toFixed(2)}%` : "\u2014";

const Badge = ({ children, bg, color }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color, textTransform: "uppercase", letterSpacing: ".5px" }}>
    {children}
  </span>
);

const KPI = ({ label, value, sublabel, color, icon }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 22px", flex: 1, minWidth: 180 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{icon} {label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color || C.text }}>{value}</div>
    {sublabel && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sublabel}</div>}
  </div>
);

const InputField = ({ label, value, onChange, type = "text", placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, boxSizing: "border-box" }} />
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, boxSizing: "border-box" }}>
      <option value="">{placeholder || "Select\u2026"}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Modal = ({ open, onClose, title, children, width = 520 }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, width, maxWidth: "95vw", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 17, color: C.text, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>{"\u2715"}</button>
        </div>
        <div style={{ padding: "20px 24px" }}>{children}</div>
      </div>
    </div>
  );
};

const ActionBtn = ({ label, color, onClick, outline }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(); }} style={{
    padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer",
    border: outline ? `1px solid ${color}` : "none",
    background: outline ? "transparent" : color,
    color: outline ? color : "#fff", marginLeft: 4,
  }}>{label}</button>
);

export default function FeeManager() {
  const [investments, setInvestments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [dealTeamMembers, setDealTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("investments");
  const [toast, setToast] = useState(null);

  // Add/Edit Recipient Modal
  const [recipientModal, setRecipientModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientForm, setRecipientForm] = useState({ recipient_name: "", recipient_type: "Employee", split_pct: "", email: "", effective_date: new Date().toISOString().slice(0, 10), deal: "" });

  // Add Fee Line Modal
  const [feeLineModal, setFeeLineModal] = useState(null);
  const [feeForm, setFeeForm] = useState({ fee_pct: "", recipient_type: "DealTeam", notes: "" });

  // Edit Investment Modal
  const [editInvModal, setEditInvModal] = useState(null);
  const [editInvForm, setEditInvForm] = useState({ invested_amount: "", ownership_pct: "", incentive_fee_pct: "" });

  // Fee Calculation
  const [feePreview, setFeePreview] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, recRes, dealRes, dtRes] = await Promise.all([
        api.get("/investors/investments/?limit=500"),
        api.get("/investors/fee-pool-recipients/?limit=100"),
        api.get("/securities/deals/?limit=500"),
        api.get("/securities/deal-team/"),
      ]);
      setDealTeamMembers((dtRes.data.results || dtRes.data || []).filter(m => m.is_active !== false));
      setInvestments(invRes.data.results || invRes.data);
      setRecipients(recRes.data.results || recRes.data);
      setDeals(dealRes.data.results || dealRes.data);
    } catch (err) {
      console.error("Fee manager load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const enriched = investments.map(inv => {
    const deal = deals.find(d => d.id === inv.deal) || {};
    const feeLines = inv.fee_lines || [];
    const totalFeePct = feeLines.reduce((s, f) => s + Number(f.fee_pct || 0), 0);
    return { ...inv, deal_name: inv.deal_name || deal.deal_name || `Deal #${inv.deal}`, investor_name: inv.investor_name || `LP #${inv.lp_account}`, fee_line_count: feeLines.length, total_fee_pct: totalFeePct, fee_lines: feeLines };
  });

  const totalInvested = enriched.reduce((s, e) => s + Number(e.invested_amount || 0), 0);
  const avgFee = enriched.length > 0 ? enriched.reduce((s, e) => s + e.total_fee_pct, 0) / enriched.length : 0;
  const totalRecipients = recipients.filter(r => r.is_active !== false).length;

  // ── CRUD Handlers ──

  const handleSaveRecipient = async () => {
    try {
      if (editingRecipient) {
        await api.patch(`/investors/fee-pool-recipients/${editingRecipient.id}/`, recipientForm);
        showToast("Recipient updated");
      } else {
        await api.post("/investors/fee-pool-recipients/", recipientForm);
        showToast("Recipient added");
      }
      setRecipientModal(false);
      setEditingRecipient(null);
      setRecipientForm({ recipient_name: "", recipient_type: "Employee", split_pct: "", email: "", effective_date: new Date().toISOString().slice(0, 10), deal: "" });
      loadData();
    } catch (err) {
      showToast(err.response?.data ? JSON.stringify(err.response.data) : "Failed to save", "error");
    }
  };

  const handleDeleteRecipient = async (rec) => {
    if (!window.confirm(`Delete recipient "${rec.recipient_name}"?`)) return;
    try {
      await api.delete(`/investors/fee-pool-recipients/${rec.id}/`);
      showToast("Recipient deleted");
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || "Delete failed", "error");
    }
  };

  const openEditRecipient = (rec) => {
    setEditingRecipient(rec);
    setRecipientForm({
      recipient_name: rec.recipient_name || "",
      recipient_type: rec.recipient_type || "Employee",
      split_pct: rec.split_pct || "",
      email: rec.email || "",
      effective_date: rec.effective_date || new Date().toISOString().slice(0, 10),
      deal: rec.deal || "",
    });
    setRecipientModal(true);
  };

  const handleSaveFeeLine = async () => {
    try {
      await api.post("/investors/investment-fees/", { deal_investment: feeLineModal, ...feeForm });
      showToast("Fee line added");
      setFeeLineModal(null);
      setFeeForm({ fee_pct: "", recipient_type: "DealTeam", notes: "" });
      loadData();
    } catch (err) {
      showToast(err.response?.data ? JSON.stringify(err.response.data) : "Failed to save", "error");
    }
  };

  const handleDeleteFeeLine = async (feeLineId) => {
    if (!window.confirm("Delete this fee line?")) return;
    try {
      await api.delete(`/investors/investment-fees/${feeLineId}/`);
      showToast("Fee line deleted");
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || "Delete failed", "error");
    }
  };

  const handleEditInvestment = async () => {
    try {
      await api.patch(`/investors/investments/${editInvModal}/`, editInvForm);
      showToast("Investment updated");
      setEditInvModal(null);
      loadData();
    } catch (err) {
      showToast(err.response?.data ? JSON.stringify(err.response.data) : "Failed to save", "error");
    }
  };

  const handleDeleteInvestment = async (inv) => {
    if (!window.confirm(`Delete investment for "${inv.investor_name}" in "${inv.deal_name}"?\n\nThis will also delete all associated fee lines.`)) return;
    try {
      await api.delete(`/investors/investments/${inv.id}/`);
      showToast("Investment deleted");
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || "Delete failed", "error");
    }
  };

  const openEditInvestment = (inv) => {
    setEditInvModal(inv.id);
    setEditInvForm({
      invested_amount: inv.invested_amount || "",
      ownership_pct: inv.ownership_pct || "",
      incentive_fee_pct: inv.incentive_fee_pct || "",
    });
  };

  const calculateFees = async (dealId) => {
    setFeeLoading(true);
    try {
      const res = await api.get("/investors/fees/calculate/", { params: { deal_id: dealId } });
      setFeePreview(res.data);
      alert("Fee preview calculated! Total fees: $" + (res.data.total_fee || 0).toLocaleString());
    } catch (err) { alert("Fee calculation failed: " + (err.response?.data?.error || err.message)); }
    finally { setFeeLoading(false); }
  };

  const postFees = async (dealId) => {
    if (!window.confirm("Post calculated fees to the General Ledger?\nThis will create GL entries for fee accruals.")) return;
    try {
      const res = await api.post("/investors/fees/post/", { deal_id: dealId });
      alert("Fees posted to GL successfully!\nEntries created: " + (res.data.entries_created || "done"));
      setFeePreview(null);
      loadData();
    } catch (err) { alert("Fee posting failed: " + (err.response?.data?.error || err.message)); }
  };

  return (
    <div style={{ padding: "0 24px 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 2000, padding: "12px 20px", borderRadius: 8, background: toast.type === "error" ? C.red : C.green, color: "#fff", fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,.4)" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.text, margin: 0 }}>Fee Structure</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: "4px 0 0" }}>Per-deal, per-investor incentive fee routing and fee pool management</p>
        </div>
        <button onClick={() => { setEditingRecipient(null); setRecipientForm({ recipient_name: "", recipient_type: "Employee", split_pct: "", email: "", effective_date: new Date().toISOString().slice(0, 10), deal: "" }); setRecipientModal(true); }}
          style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + Add Recipient
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <KPI label="Total Invested" value={fmtUSD(totalInvested)} color={C.green} icon="💰" sublabel={`${enriched.length} investments`} />
        <KPI label="Avg Incentive Fee" value={fmtPct(avgFee)} color={C.purple} icon="📊" sublabel="Across all investments" />
        <KPI label="Fee Recipients" value={totalRecipients} icon="👥" sublabel="Active pool members" />
        <KPI label="Unique Deals" value={new Set(enriched.map(e => e.deal)).size} icon="🔍" />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[
          { key: "investments", label: "Deal Investments", count: enriched.length },
          { key: "recipients", label: "Fee Pool Recipients", count: recipients.length },
          { key: "waterfall", label: "Fee Waterfall" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "8px 20px", borderRadius: 20,
            border: `1px solid ${tab === t.key ? C.accent : C.border}`,
            background: tab === t.key ? C.accent : "transparent",
            color: tab === t.key ? "#fff" : C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            {t.label}{t.count != null ? ` (${t.count})` : ""}
          </button>
        ))}
      </div>

      {/* ── Investments Tab ── */}
      {tab === "investments" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                {["Investor", "Deal", "Amount", "Own %", "Fee Breakdown", "Total Fee", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: h === "Amount" || h === "Own %" || h === "Total Fee" ? "right" : "left", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enriched.map(inv => (
                <tr key={inv.id} style={{ borderBottom: `1px solid ${C.border}22` }}>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: C.text }}>{inv.investor_name}</td>
                  <td style={{ padding: "10px 14px", color: C.muted, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.deal_name}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600 }}>{fmtUSD(inv.invested_amount)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>{fmtPct(inv.ownership_pct)}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {inv.fee_lines && inv.fee_lines.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {inv.fee_lines.map(fl => (
                          <div key={fl.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: fl.recipient_type === "DealTeam" ? C.green : C.purple }}>
                              {fl.fee_pct}%
                            </span>
                            <span style={{ fontSize: 11, color: C.muted }}>{"\u2192"}</span>
                            <Badge bg={fl.recipient_type === "DealTeam" ? C.greenBg : C.purpleBg} color={fl.recipient_type === "DealTeam" ? C.green : C.purple}>
                              {fl.recipient_type === "DealTeam" ? "Deal Team" : "Mgmt Co"}
                            </Badge>
                            <button onClick={() => handleDeleteFeeLine(fl.id)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11, padding: "0 2px", opacity: 0.6 }} title="Delete fee line">{"\u2715"}</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>No fee lines</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: inv.total_fee_pct > 0 ? C.purple : C.muted }}>{fmtPct(inv.total_fee_pct)}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      <ActionBtn label="Edit" color={C.accent} onClick={() => openEditInvestment(inv)} outline />
                      <ActionBtn label="+ Fee" color={C.green} onClick={() => setFeeLineModal(inv.id)} outline />
                      <ActionBtn label="Del" color={C.red} onClick={() => handleDeleteInvestment(inv)} outline />
                    </div>
                  </td>
                </tr>
              ))}
              {enriched.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: C.muted }}>No deal investments found. Create investments via the Deal Wizard.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Recipients Tab ── */}
      {tab === "recipients" && (
        <FinTable
          columns={[
            { key: "recipient_name", label: "Name", sortable: true },
            { key: "recipient_type", label: "Type", sortable: true,
              render: (v) => {
                const colors = { ManagementCompany: { bg: C.purpleBg, c: C.purple }, Employee: { bg: C.greenBg, c: C.green }, Advisor: { bg: C.orangeBg, c: C.orange } };
                const s = colors[v] || { bg: C.cyanBg, c: C.cyan };
                return <Badge bg={s.bg} color={s.c}>{v}</Badge>;
              }},
            { key: "split_pct", label: "Pool Split %", align: "right", sortable: true, fmt: fmtPct },
            { key: "email", label: "Email", sortable: true, truncate: 25 },
            { key: "effective_date", label: "Effective", sortable: true },
            { key: "is_active", label: "Status", sortable: true,
              render: (v) => v !== false ? <Badge bg={C.greenBg} color={C.green}>Active</Badge> : <Badge bg={C.redBg} color={C.red}>Inactive</Badge> },
          ]}
          rows={recipients}
          loading={loading}
          pageSize={25}
          defaultSort={{ key: "split_pct", direction: "desc" }}
          emptyMessage="No fee pool recipients configured. Add recipients to split the deal team fee pool."
          actionColumn={(rec) => (
            <div style={{ display: "flex", gap: 2 }}>
              <ActionBtn label="Edit" color={C.accent} onClick={() => openEditRecipient(rec)} outline />
              <ActionBtn label="Del" color={C.red} onClick={() => handleDeleteRecipient(rec)} outline />
            </div>
          )}
        />
      )}

      {/* ── Waterfall Tab ── */}
      {tab === "waterfall" && (
        <div style={{ padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Fee Waterfall Logic</h3>
          {[
            { step: 1, title: "Equity Sold", desc: "Realized P&L computed per TaxLot (sell price - cost basis)" },
            { step: 2, title: "P&L Allocated to Investors", desc: "Pro-rata by ownership_pct on DealInvestment" },
            { step: 3, title: "Fee Calculated per Investor", desc: "Each investor's P&L share x sum(DealInvestmentFee.fee_pct)" },
            { step: 4, title: "Fee Split by Recipient Type", desc: "DealTeam vs ManagementCompany routing per fee line" },
            { step: 5, title: "Deal Team Pool Split", desc: "DealTeam fees further split by FeePoolRecipient.split_pct" },
            { step: 6, title: "GL Entries Posted", desc: "Fee accrual + distribution posted to double-entry ledger" },
          ].map(s => (
            <div key={s.step} style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#345479", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{s.step}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: "#6b6b6b" }}>{s.desc}</div>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: 16, background: "#f5f2ef", borderRadius: 8, border: "1px solid #d9d3cc" }}>
            <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>Calculate & Post Fees</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {[...new Set((investments || []).map(i => i.deal))].filter(Boolean).map(dealId => {
                const dealName = (investments || []).find(i => i.deal === dealId)?.deal_name || ("Deal " + dealId);
                return (
                  <div key={dealId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{dealName}:</span>
                    <button onClick={() => calculateFees(dealId)} disabled={feeLoading}
                      style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #345479", background: "transparent", color: "#345479", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      {feeLoading ? "Calculating..." : "Calculate Fees"}
                    </button>
                    <button onClick={() => postFees(dealId)}
                      style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#345479", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      Post to GL
                    </button>
                  </div>
                );
              })}
            </div>
            {feePreview && (
              <div style={{ marginTop: 16, padding: 12, background: "#fff", borderRadius: 6 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Fee Preview</div>
                <div>Total Fee: <strong>${Number(feePreview.total_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
                {feePreview.deal_team_total != null && <div>Deal Team: ${Number(feePreview.deal_team_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
                {feePreview.mgmt_co_total != null && <div>Management Co: ${Number(feePreview.mgmt_co_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
                {feePreview.investor_allocations && feePreview.investor_allocations.map((a, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#6b6b6b" }}>
                    {a.investor_name}: ${Number(a.fee_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add/Edit Recipient Modal ── */}
      <Modal open={recipientModal} onClose={() => { setRecipientModal(false); setEditingRecipient(null); }} title={editingRecipient ? "Edit Recipient" : "Add Fee Pool Recipient"}>
        <SelectField label="Deal" value={recipientForm.deal} onChange={v => setRecipientForm(p => ({ ...p, deal: v }))} placeholder="Select deal..."
          options={(investments || []).reduce((acc, inv) => { if (!acc.find(a => a.value === inv.deal)) acc.push({ value: inv.deal, label: inv.deal_name || ("Deal " + inv.deal) }); return acc; }, [])} />
        <SelectField label="Name" value={recipientForm.recipient_name} onChange={v => setRecipientForm(p => ({ ...p, recipient_name: v }))} placeholder="Select team member..."
          options={dealTeamMembers.map(m => ({ value: m.member_name, label: m.member_name + (m.role ? ` (${m.role})` : '') }))} />
        <SelectField label="Type" value={recipientForm.recipient_type} onChange={v => setRecipientForm(p => ({ ...p, recipient_type: v }))}
          options={[{ value: "ManagementCompany", label: "Management Company" }, { value: "Employee", label: "Employee" }, { value: "Advisor", label: "Advisor" }, { value: "Other", label: "Other" }]} />
        <InputField label="Pool Split %" type="pct" value={recipientForm.split_pct} onChange={v => setRecipientForm(p => ({ ...p, split_pct: v }))} placeholder="e.g. 25.00" />
        <InputField label="Email" value={recipientForm.email} onChange={v => setRecipientForm(p => ({ ...p, email: v }))} placeholder="name@company.com" />
        <InputField label="Effective Date" type="date" value={recipientForm.effective_date} onChange={v => setRecipientForm(p => ({ ...p, effective_date: v }))} />
        <button onClick={handleSaveRecipient} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
          {editingRecipient ? "Save Changes" : "Add Recipient"}
        </button>
        {editingRecipient && (
          <button onClick={() => { handleDeleteRecipient(editingRecipient); setRecipientModal(false); setEditingRecipient(null); }}
            style={{ width: "100%", padding: 12, borderRadius: 8, border: `1px solid ${C.red}`, background: "transparent", color: C.red, fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
            Delete Recipient
          </button>
        )}
      </Modal>

      {/* ── Add Fee Line Modal ── */}
      <Modal open={!!feeLineModal} onClose={() => setFeeLineModal(null)} title="Add Fee Line">
        <InputField label="Fee %" type="pct" value={feeForm.fee_pct} onChange={v => setFeeForm(p => ({ ...p, fee_pct: v }))} placeholder="e.g. 15.00 for 15%" />
        <SelectField label="Recipient Type" value={feeForm.recipient_type} onChange={v => setFeeForm(p => ({ ...p, recipient_type: v }))}
          options={[{ value: "DealTeam", label: "Deal Team" }, { value: "ManagementCompany", label: "Management Company" }]} />
        <InputField label="Notes" value={feeForm.notes} onChange={v => setFeeForm(p => ({ ...p, notes: v }))} placeholder="Optional\u2026" />
        <button onClick={handleSaveFeeLine} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
          Add Fee Line
        </button>
        {/* Show existing fee lines for this investment */}
        {feeLineModal && (() => {
          const inv = enriched.find(e => e.id === feeLineModal);
          if (!inv || !inv.fee_lines || inv.fee_lines.length === 0) return null;
          return (
            <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8 }}>EXISTING FEE LINES</div>
              {inv.fee_lines.map(fl => (
                <div key={fl.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}22` }}>
                  <span style={{ fontSize: 13 }}>{fl.fee_pct}% {"\u2192"} {fl.recipient_type}</span>
                  <ActionBtn label="Delete" color={C.red} onClick={() => handleDeleteFeeLine(fl.id)} outline />
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>

      {/* ── Edit Investment Modal ── */}
      <Modal open={!!editInvModal} onClose={() => setEditInvModal(null)} title="Edit Investment">
        <InputField label="Invested Amount ($)" type="number" value={editInvForm.invested_amount} onChange={v => setEditInvForm(p => ({ ...p, invested_amount: v }))} />
        <InputField label="Ownership %" type="pct" value={editInvForm.ownership_pct} onChange={v => setEditInvForm(p => ({ ...p, ownership_pct: v }))} />
        <InputField label="Incentive Fee %" type="pct" value={editInvForm.incentive_fee_pct} onChange={v => setEditInvForm(p => ({ ...p, incentive_fee_pct: v }))} />
        <button onClick={handleEditInvestment} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
          Save Changes
        </button>
      </Modal>
    </div>
  );
}
